import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useDispatch } from 'react-redux'
import Swal from 'sweetalert2'
import moment from 'moment'
import { addCouponCodes } from '../../slice/couponCodeSlice'

// Validation Schema
const couponSchema = yup.object().shape({
    name: yup
        .string()
        .required('Coupon code is required')
        .min(3, 'Coupon code must be at least 3 characters')
        .max(30, 'Coupon code cannot exceed 30 characters')
        .matches(/^[A-Z0-9_-]+$/, 'Coupon code can only contain uppercase letters, numbers, hyphens, and underscores')
        .test('uppercase', 'Coupon code must be in uppercase', (value) => {
            return value === value?.toUpperCase()
        }),
    
    description: yup
        .string()
        .required('Description is required')
        .min(10, 'Description must be at least 10 characters')
        .max(200, 'Description cannot exceed 200 characters'),
    
    discount_percent: yup
        .number()
        .required('Discount percentage is required')
        .min(1, 'Discount must be at least 1%')
        .max(100, 'Discount cannot exceed 100%')
        .typeError('Discount must be a number'),
    
    valid_from: yup
        .date()
        .required('Start date is required')
        .typeError('Please enter a valid date'),
    
    valid_to: yup
        .date()
        .required('End date is required')
        .typeError('Please enter a valid date')
        .min(yup.ref('valid_from'), 'End date must be after start date')
        .test('minDuration', 'Coupon must be valid for at least 1 day', function(value) {
            const { valid_from } = this.parent
            if (!valid_from || !value) return true
            const duration = moment(value).diff(moment(valid_from), 'hours')
            return duration >= 24
        }),
})

const AddCoupon = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        reset,
        setValue
    } = useForm({
        resolver: yupResolver(couponSchema),
        defaultValues: {
            name: '',
            description: '',
            discount_percent: '',
            valid_from: '',
            valid_to: '',
        }
    })

    // Watch dates for validation feedback
    const validFrom = watch('valid_from')
    const validTo = watch('valid_to')

    // Calculate duration
    const getDuration = () => {
        if (validFrom && validTo) {
            const days = moment(validTo).diff(moment(validFrom), 'days')
            return days > 0 ? `${days} day${days !== 1 ? 's' : ''}` : null
        }
        return null
    }

    // Handle coupon code input to auto-uppercase
    const handleCouponCodeChange = (e) => {
        const value = e.target.value.toUpperCase()
        setValue('name', value)
    }

    const onSubmit = async (data) => {
        setIsSubmitting(true)

        try {
            // Build JSON payload
            const payload = {
                name: data.name,
                description: data.description,
                discount_percent: Number(data.discount_percent),
                valid_from: moment(data.valid_from).toISOString(),
                valid_to: moment(data.valid_to).toISOString(),
            }

            // Dispatch Redux action
            const response = await dispatch(addCouponCodes(payload))

            if (response?.payload?.code === 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: response?.payload?.message || 'Coupon created successfully',
                    timer: 2000,
                    showConfirmButton: false,
                })

                reset()
                setTimeout(() => navigate('/coupons'), 2000)
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response?.payload?.message || 'Failed to create coupon',
                })
            }

        } catch (error) {
            console.error(error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.message || 'Something went wrong',
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Add Coupon Code</h4>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/coupons')}
                    style={{
                        padding: '8px 20px',
                        fontSize: '14px',
                        borderRadius: '6px'
                    }}
                >
                    ← Back to List
                </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="row">
                    {/* Coupon Information */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                Coupon Information
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {/* Coupon Code */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Coupon Code <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                            placeholder="e.g., SUMMER2026"
                                            {...register('name')}
                                            onChange={handleCouponCodeChange}
                                            style={{
                                                fontFamily: 'monospace',
                                                fontWeight: '600',
                                                letterSpacing: '0.5px'
                                            }}
                                        />
                                        {errors.name && (
                                            <div className="invalid-feedback">{errors.name.message}</div>
                                        )}
                                        <small className="text-muted">
                                            Use uppercase letters, numbers, hyphens, and underscores only
                                        </small>
                                    </div>

                                    {/* Discount Percentage */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Discount Percentage <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <input
                                                type="number"
                                                className={`form-control ${errors.discount_percent ? 'is-invalid' : ''}`}
                                                placeholder="Enter discount percentage"
                                                {...register('discount_percent')}
                                                min="1"
                                                max="100"
                                                step="1"
                                            />
                                            <span className="input-group-text">%</span>
                                            {errors.discount_percent && (
                                                <div className="invalid-feedback">{errors.discount_percent.message}</div>
                                            )}
                                        </div>
                                        <small className="text-muted">
                                            Enter a value between 1 and 100
                                        </small>
                                    </div>

                                    {/* Description */}
                                    <div className="col-12">
                                        <label className="form-label">
                                            Description <span className="text-danger">*</span>
                                        </label>
                                        <textarea
                                            className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                                            placeholder="Describe the coupon offer (e.g., 25% discount on all premium subscriptions)"
                                            rows="3"
                                            {...register('description')}
                                        />
                                        {errors.description && (
                                            <div className="invalid-feedback">{errors.description.message}</div>
                                        )}
                                        <small className="text-muted">
                                            Minimum 10 characters, maximum 200 characters
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Validity Period */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                Validity Period
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {/* Valid From */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Valid From <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="datetime-local"
                                            className={`form-control ${errors.valid_from ? 'is-invalid' : ''}`}
                                            {...register('valid_from')}
                                        />
                                        {errors.valid_from && (
                                            <div className="invalid-feedback">{errors.valid_from.message}</div>
                                        )}
                                        <small className="text-muted">
                                            Start date and time for coupon validity
                                        </small>
                                    </div>

                                    {/* Valid To */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Valid To <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="datetime-local"
                                            className={`form-control ${errors.valid_to ? 'is-invalid' : ''}`}
                                            {...register('valid_to')}
                                        />
                                        {errors.valid_to && (
                                            <div className="invalid-feedback">{errors.valid_to.message}</div>
                                        )}
                                        <small className="text-muted">
                                            End date and time for coupon validity
                                        </small>
                                    </div>

                                    {/* Duration Display */}
                                    {getDuration() && (
                                        <div className="col-12">
                                            <div className="alert alert-info mb-0" style={{ borderRadius: '6px' }}>
                                                <i className="fe-info" style={{ fontSize: '16px' }}></i>
                                                <strong className="ms-2">Coupon Duration:</strong> {getDuration()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="d-flex gap-3 justify-content-end">
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => navigate('/coupons')}
                        disabled={isSubmitting}
                        style={{
                            padding: '10px 24px',
                            fontSize: '14px',
                            borderRadius: '6px'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-success"
                        disabled={isSubmitting}
                        style={{
                            padding: '10px 24px',
                            fontSize: '14px',
                            borderRadius: '6px',
                            minWidth: '120px'
                        }}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Creating...
                            </>
                        ) : (
                            'Create Coupon'
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}

export default AddCoupon;